import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CFG = { withCredentials: true };

const _unwrap = (p) => p.then((r) => r.data);

// PM portal endpoints (used by PM/AM/MS roles)
export const pmApi = {
  me:                       ()             => _unwrap(axios.get(`${API}/pm/me`, CFG)),
  dashboard:                ()             => _unwrap(axios.get(`${API}/pm/dashboard`, CFG)),
  properties:               ()             => _unwrap(axios.get(`${API}/pm/properties`, CFG)),
  propertyDetail:           (id)           => _unwrap(axios.get(`${API}/pm/properties/${id}`, CFG)),
  submitRequest:            (body)         => _unwrap(axios.post(`${API}/pm/service-requests`, body, CFG)),
  listRequests:             (params={})    => _unwrap(axios.get(`${API}/pm/service-requests`, { ...CFG, params })),
  requestDetail:            (id)           => _unwrap(axios.get(`${API}/pm/service-requests/${id}`, CFG)),
  invoices:                 ()             => _unwrap(axios.get(`${API}/pm/invoices`, CFG)),
  serviceHistory:           (params={})    => _unwrap(axios.get(`${API}/pm/service-history`, { ...CFG, params })),
  contacts:                 ()             => _unwrap(axios.get(`${API}/pm/contacts`, CFG)),
  // Unlisted/pending requester review (Contact Review Needed)
  pendingContacts:          (status='open')=> _unwrap(axios.get(`${API}/pm/pending-contacts`, { ...CFG, params: { status } })),
  confirmPendingContact:    (id, body={})  => _unwrap(axios.post(`${API}/pm/pending-contacts/${id}/confirm`, body, CFG)),
  keepPendingExternal:      (id)           => _unwrap(axios.post(`${API}/pm/pending-contacts/${id}/external`, {}, CFG)),
  // Phase 22 — Purchase Order
  poSettingsForProperty:    (propId)       => _unwrap(axios.get(`${API}/pm/po-settings/property/${propId}`, CFG)),
  updateRequestPo:          (id, body)     => _unwrap(axios.patch(`${API}/pm/service-requests/${id}/po`, body, CFG)),
  uploadPoFile:             (file)         => {
    const fd = new FormData(); fd.append('file', file);
    return _unwrap(axios.post(`${API}/pm/po-files/upload`, fd, { ...CFG, headers: { 'Content-Type': 'multipart/form-data' } }));
  },
  poFileUrl:                (fileId)       => `${API}/pm/po-files/${fileId}`,
  // Property requests (proposed new property, admin must approve)
  submitPropertyRequest:    (body)         => _unwrap(axios.post(`${API}/pm/property-requests`, body, CFG)),
  listPropertyRequests:     ()             => _unwrap(axios.get(`${API}/pm/property-requests`, CFG)),
};

// Admin-side PM management endpoints
export const pmAdminApi = {
  companies: {
    list:   (includeInactive=false) => _unwrap(axios.get(`${API}/admin/pm/companies`, { ...CFG, params: { include_inactive: includeInactive } })),
    create: (body)                  => _unwrap(axios.post(`${API}/admin/pm/companies`, body, CFG)),
    update: (id, body)              => _unwrap(axios.put(`${API}/admin/pm/companies/${id}`, body, CFG)),
    archive:(id)                    => _unwrap(axios.delete(`${API}/admin/pm/companies/${id}`, CFG)),
  },
  properties: {
    list:   (companyId)             => _unwrap(axios.get(`${API}/admin/pm/properties`, { ...CFG, params: companyId ? { company_id: companyId } : {} })),
    create: (body)                  => _unwrap(axios.post(`${API}/admin/pm/properties`, body, CFG)),
    update: (id, body)              => _unwrap(axios.put(`${API}/admin/pm/properties/${id}`, body, CFG)),
    archive:(id)                    => _unwrap(axios.delete(`${API}/admin/pm/properties/${id}`, CFG)),
  },
  users: {
    list:        (companyId)        => _unwrap(axios.get(`${API}/admin/pm/users`, { ...CFG, params: companyId ? { company_id: companyId } : {} })),
    create:      (body)             => _unwrap(axios.post(`${API}/admin/pm/users`, body, CFG)),
    update:      (id, body)         => _unwrap(axios.put(`${API}/admin/pm/users/${id}`, body, CFG)),
    resetPw:     (id)               => _unwrap(axios.post(`${API}/admin/pm/users/${id}/reset-password`, {}, CFG)),
    disable:     (id)               => _unwrap(axios.delete(`${API}/admin/pm/users/${id}`, CFG)),
    // Phase 2 — auth controls
    sendResetLink: (id)             => _unwrap(axios.post(`${API}/admin/pm/users/${id}/send-reset-link`, {}, CFG)),
    invite:        (id)             => _unwrap(axios.post(`${API}/admin/pm/users/${id}/invite`, {}, CFG)),
    cancelInvite:  (id)             => _unwrap(axios.post(`${API}/admin/pm/users/${id}/cancel-invite`, {}, CFG)),
    changeEmail:   (id, body)       => _unwrap(axios.post(`${API}/admin/pm/users/${id}/change-email`, body, CFG)),
    setPrimary:    (id, isPrimary)  => _unwrap(axios.post(`${API}/admin/pm/users/${id}/primary`, { is_primary: isPrimary }, CFG)),
    enable:        (id)             => _unwrap(axios.post(`${API}/admin/pm/users/${id}/enable`, {}, CFG)),
    disableWithReason: (id, reason) => _unwrap(axios.post(`${API}/admin/pm/users/${id}/disable`, { reason }, CFG)),
    lock:          (id, reason)     => _unwrap(axios.post(`${API}/admin/pm/users/${id}/lock`, { reason }, CFG)),
    unlock:        (id)             => _unwrap(axios.post(`${API}/admin/pm/users/${id}/unlock`, {}, CFG)),
    accountAudit:  (id)             => _unwrap(axios.get(`${API}/admin/pm/users/${id}/account-audit`, CFG)),
  },
  requests: {
    list:           (params={})     => _unwrap(axios.get(`${API}/admin/pm/service-requests`, { ...CFG, params })),
    updateStatus:   (id, body)      => _unwrap(axios.patch(`${API}/admin/pm/service-requests/${id}/status`, body, CFG)),
    assignTech:     (id, body)      => _unwrap(axios.patch(`${API}/admin/pm/service-requests/${id}/assign`, body, CFG)),
  },
  crm: {
    widgets:        ()              => _unwrap(axios.get(`${API}/admin/pm/crm/widgets`, CFG)),
    followupsDue:   ()              => _unwrap(axios.get(`${API}/admin/pm/crm/followups-due`, CFG)),
    logContact:     (id, nextDate)  => _unwrap(axios.post(`${API}/admin/pm/crm/${id}/log-contact`, { next_date: nextDate || null }, CFG)),
    list:           (params={})     => _unwrap(axios.get(`${API}/admin/pm/crm`, { ...CFG, params })),
    create:         (body)          => _unwrap(axios.post(`${API}/admin/pm/crm`, body, CFG)),
    update:         (id, body)      => _unwrap(axios.put(`${API}/admin/pm/crm/${id}`, body, CFG)),
    archive:        (id)            => _unwrap(axios.delete(`${API}/admin/pm/crm/${id}`, CFG)),
    convert:        (id)            => _unwrap(axios.post(`${API}/admin/pm/crm/${id}/convert`, {}, CFG)),
    audit:          (id)            => _unwrap(axios.get(`${API}/admin/pm/crm/${id}/audit`, CFG)),
  },
  propertyRequests: {
    list:     (status)   => _unwrap(axios.get(`${API}/admin/pm/property-requests`, { ...CFG, params: status ? { status } : {} })),
    decide:   (id, body) => _unwrap(axios.post(`${API}/admin/pm/property-requests/${id}/decision`, body, CFG)),
  },
};

export const PRIORITIES = ['Emergency', 'High', 'Normal', 'Low'];
export const STATUS_FLOW = [
  'Submitted', 'Scheduled', 'Technician Assigned', 'En Route',
  'On Site', 'Waiting Approval', 'Completed', 'Invoiced', 'Paid',
];
export const PM_ROLES = [
  { value: 'property_manager',       label: 'Property Manager' },
  { value: 'asset_manager',          label: 'Asset Manager' },
  { value: 'maintenance_supervisor', label: 'Maintenance Supervisor' },
];
export const CONTACT_ROLES = [
  { value: 'property_manager',       label: 'Property Manager' },
  { value: 'maintenance_supervisor', label: 'Maintenance Supervisor' },
  { value: 'emergency',              label: 'Emergency Contact' },
  { value: 'billing',                label: 'Billing Contact' },
];

export const CRM_COMPANY_TYPES = [
  'Property Management', 'Apartment Community', 'Senior Living',
  'Commercial Facility', 'HOA', 'Other',
];
export const CRM_LEAD_STATUSES = [
  'Prospect', 'Contacted', 'Vendor Packet Sent', 'In Review',
  'Approved Vendor', 'Active Customer', 'Not Interested',
];


// Public (unauthenticated) PM password reset / invite set-password flow
export const pmResetApi = {
  validate: (token)                => _unwrap(axios.post(`${API}/pm/reset/validate`, { token })),
  complete: (token, newPassword)   => _unwrap(axios.post(`${API}/pm/reset/complete`, { token, new_password: newPassword })),
};
